import React, { useEffect, useMemo, useState } from "react";
import { PageContainer } from '@amzn/stencil-react-components/page';
import { Col, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import {P, H3, Label } from "@amzn/stencil-react-components/text";
import { Radio, Select, TextArea } from "@amzn/stencil-react-components/form";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { getProcessedEvents, getTimeFromEpoch, retryFailedEvents } from "src/service/service";
import { Stage } from "src/enum/enums";
import { IconReset, IconSize } from '@amzn/stencil-react-components/icons';
import { IProcessedEvent } from "src/entity/entities";
import { Pagination } from '@amzn/stencil-react-components/pagination';
import { Table, TableSpacing } from "@amzn/stencil-react-components/table";
import { getStage } from "src/util/commonUtil";
import retryableTriggers from "src/config/retryableTriggers.json";
import "src/components/commonStyles.scss";

const STRINGS_DISPLAYED = {
  select_event_label: "Select an event",
  next_button: "Next",
  error_fetching_event_models: "Error fetching your event models. Please try again later.",
  trigger_label: "Trigger",
  trigger_placeholder: "Choose a trigger",
  successful: "Successful",
  failed: "Failed",
  processing: "Processing",
  retry_event: "Retry Event",
  not_supported: "Not Supported",
  no_events_found: "No events found",
  created: "Created",
  updated: "Updated",
  payload: "Payload",
  response: "Response",
  retry: "Retry",
  display_header: `Events for {triggerName} are`
}

/**
 * Renders the page to let users explore different events for a particular trigger and retry 
 * the failed ones if needed.
 */
export const ReplayEventModelPage = () => {

  const [processedEvents, setProcessedEvents] = useState<IProcessedEvent[]>([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPage, setSelectedPage] = useState(1);
  const [retryId, setRetryId] = useState("");
  const [triggerName, setTriggerName] = useState<string>("");

  const triggerOptions = Object.keys(retryableTriggers)

  const selectedData = useMemo(() => {
      const selectedData = [];
      processedEvents?.forEach( (event) => {
        if(selectedStatus?.toLowerCase() == event?.status?.toLowerCase()){
          selectedData?.push(event);
        }
      })
      return selectedData;
  }, [processedEvents, selectedStatus]);

  const processPayload = (data) => {
      return (
        <TextArea id={data.id} resize={"both"} width={"100%"} disabled value={data?.payload}/> 
      )
  }

  const ButtonCell = ({ data }) => (
    selectedStatus == STRINGS_DISPLAYED.failed.toLowerCase() ? 
    <Radio key={data} checked={retryId == data ? true : false}
      onClick={()=> setRetryId(data)} name={"radioButton"} /> : 
     <> {STRINGS_DISPLAYED.not_supported} </>
  );

  const columns = [
    { header: STRINGS_DISPLAYED.created, accessor: ({ data }) => getTimeFromEpoch(data?.created), width: '10%' },
    { header: STRINGS_DISPLAYED.updated, accessor: ({ data }) => getTimeFromEpoch(data?.updated), width: '10%' },
    { header: STRINGS_DISPLAYED.payload, accessor: ({ data }) => processPayload(data) , width: '50%'},
    { header: STRINGS_DISPLAYED.response, accessor: ({ data }) => data?.response, width: '20%' },
    {
      header: STRINGS_DISPLAYED.retry,
      accessor: 'id',
      cellComponent: ButtonCell,
      hasReducedSpacing: true,
      width: '10%'
    }
  ];

  const callProcessedEventsAPI = () => {
    getProcessedEvents(getStage() as Stage, triggerName)
    .then((events) => {
      setProcessedEvents(events);
    });
  } 

  const onStatusButtonClick = (status : string) => {
    setSelectedStatus(status);
  }

  useEffect(() => {
    setProcessedEvents([]);
    setSelectedPage(1);
    callProcessedEventsAPI();
  }, [triggerName]);

  const onRetryButtonClick = () => {
    retryFailedEvents(getStage() as Stage, retryId)
      .then(() => {
        callProcessedEventsAPI();
      }).finally(() => {
        setRetryId("");
      })
  }

  return (
    <PageContainer backgroundColor={"white"}>
        <Col gridGap={"24px"} height={"100%"}>
            <Row 
                alignItems={"center"} 
                justifyContent={"space-between"}
                width={"fit-content"}
            >
                <Label 
                    fontSize={"20px"} 
                    fontWeight={"bold"} 
                    htmlFor="event-trigger"
                    className={"width_200_px"}
                >
                    {STRINGS_DISPLAYED.trigger_label}
                </Label>
                <Select
                    id={"event-trigger"}
                    options={triggerOptions}
                    onChange={(value) => {
                        setTriggerName(value);
                        setSelectedStatus("");
                    }}
                    width={"400px"}
                    placeholder={STRINGS_DISPLAYED.trigger_placeholder}
                />
            </Row>
            <Spacer height={"32px"}/>
            {triggerName != "" && 
                <View style={{width :"100%"}}>
                    <H3>{STRINGS_DISPLAYED.display_header.replace("{triggerName}", `"${triggerName}"`)} </H3>
                    <Spacer height={"36px"}/>
                     <Row columnGap={"36px"}>
                      <Button variant={selectedStatus == STRINGS_DISPLAYED.successful.toLowerCase() ? 
                        ButtonVariant.Primary : null} onClick={() => 
                          onStatusButtonClick(STRINGS_DISPLAYED.successful.toLowerCase())
                        }> 
                        {STRINGS_DISPLAYED.successful} 
                      </Button>
                      <Button variant={selectedStatus == STRINGS_DISPLAYED.failed.toLowerCase() ? 
                        ButtonVariant.Primary : null}  onClick={() => 
                          onStatusButtonClick(STRINGS_DISPLAYED.failed.toLowerCase())
                        }> 
                        {STRINGS_DISPLAYED.failed} 
                      </Button>
                      <Button variant={selectedStatus == STRINGS_DISPLAYED.processing.toLowerCase() ? 
                        ButtonVariant.Primary : null} onClick={() => 
                          onStatusButtonClick(STRINGS_DISPLAYED.processing.toLowerCase())
                        }> 
                        {STRINGS_DISPLAYED.processing} 
                      </Button>
                      <Button disabled={retryId.trim().length > 0 ? false : true} 
                        icon={<IconReset size={IconSize.Small}/>} onClick={() => onRetryButtonClick()}> 
                        {STRINGS_DISPLAYED.retry_event} 
                      </Button>
                    </Row>
                    
                    <Spacer height={"36px"}/>
                    {
                    <Col>
                    { selectedStatus == "" ? <> </> : selectedData?.length ?
                      <>
                      <Table spacing={TableSpacing.Reduced} columns={columns}
                        hasStickyHeader={true} data={selectedData?.slice((selectedPage-1)*5, selectedPage*5)} />
                      <Spacer height={"24px"}></Spacer>
                      <Row alignSelf="center">
                        <Pagination
                          numberOfPages={Math.ceil(selectedData?.length/5)}
                          selectedPage={selectedPage}
                          onPageSelect={setSelectedPage}
                        />
                      </Row>
                    </> : <P> {STRINGS_DISPLAYED.no_events_found} </P>
                    }
                    </Col>
                    }
                </View>
            }
        </Col>
    </PageContainer>
  )
}

